import { useRef } from 'react';
import type { MessageSummary } from 'zitimail-shared-types';

import {
  ArchiveIcon,
  EnvelopeOpenIcon,
  FlagIcon,
  PaperclipIcon,
  TrashIcon,
} from '../components/icons';
import { chineseScript, displayName, hue, initials, listDate, type Conversation } from './format';

export type RowAction = 'archive' | 'delete' | 'toggle-read' | 'toggle-flag';

/** How long a press is held, still, to select its row. */
export const LONG_PRESS_MS = 500;
/** How far a press may wander and still be a press, not a drag or a scroll. */
const PRESS_SLOP_PX = 8;

/** The drag type a message row carries: only our own folders accept it. */
export const MESSAGES_DRAG = 'application/x-genworx-messages';

/**
 * The message list: one row per conversation (or per message, ungrouped).
 * Selection as in a desktop mail app: Ctrl-click (Cmd on a Mac) adds a row,
 * Shift-click a range, and the avatar is a checkbox (shown on hover, and on
 * every row once anything is selected). As in phone mail apps, a long press
 * selects a row, and on a touchscreen each tap then adds or removes one.
 * Hovering a row shows its quick actions. A row can be dragged onto a folder (with the rest of the
 * selection, when it is selected), and right-clicked for its menu.
 */
export function MessageList({
  rows,
  openKey,
  focusKey,
  selected,
  me,
  onOpen,
  onToggle,
  onAction,
  onDragRows,
  onMenu,
}: {
  rows: readonly Conversation[];
  openKey: string | null;
  focusKey: string | null;
  selected: ReadonlySet<string>;
  me: string;
  onOpen: (row: Conversation) => void;
  onToggle: (row: Conversation, range: boolean) => void;
  onAction: (row: Conversation, action: RowAction) => void;
  /** A drag starts on `row`: the rows it carries (the selection, or the row). */
  onDragRows?: (row: Conversation) => readonly Conversation[];
  /** Right-click (or the menu key) on `row`, at that point. */
  onMenu?: (row: Conversation, at: { x: number; y: number }) => void;
}): JSX.Element {
  // The press being held: its row, where it started, and its timer.
  const press = useRef<{ key: string; x: number; y: number; timer: number } | null>(null);
  // The press that selected a row: the click (and on touch the menu) it
  // ends with is not also an open.
  const pressed = useRef<string | null>(null);
  // Whether the last press was a finger: then a tap while selecting selects.
  const touch = useRef(false);
  const release = () => {
    if (press.current) {
      window.clearTimeout(press.current.timer);
      press.current = null;
    }
  };
  return (
    <ul
      className={selected.size > 0 ? 'msg-list selecting' : 'msg-list'}
      role="listbox"
      aria-label="Messages"
      aria-multiselectable="true"
    >
      {rows.map((row) => {
        const key = rowKey(row.latest);
        const sender = row.latest.from;
        const names = row.people
          .slice(0, 3)
          .map((p) => (p.address.toLowerCase() === me.toLowerCase() ? 'me' : displayName(p)))
          .join(', ');
        const isSelected = selected.has(key);
        return (
          <li
            key={key}
            role="option"
            aria-selected={openKey === key}
            className={[
              'msg-row',
              row.unread ? 'unread' : '',
              openKey === key ? 'open' : '',
              focusKey === key ? 'focus' : '',
              isSelected ? 'selected' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onPointerDown={(e) => {
              touch.current = e.pointerType !== 'mouse';
              pressed.current = null;
              release();
              if (e.button !== 0) {
                return;
              }
              press.current = {
                key,
                x: e.clientX,
                y: e.clientY,
                timer: window.setTimeout(() => {
                  press.current = null;
                  pressed.current = key;
                  if (!isSelected) {
                    onToggle(row, false);
                  }
                }, LONG_PRESS_MS),
              };
            }}
            onPointerMove={(e) => {
              const p = press.current;
              if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > PRESS_SLOP_PX) {
                release();
              }
            }}
            onPointerUp={release}
            onPointerCancel={release}
            onPointerLeave={release}
            onClick={(e) => {
              if (pressed.current === key) {
                pressed.current = null;
                return;
              }
              if (e.metaKey || e.ctrlKey) {
                onToggle(row, false);
              } else if (e.shiftKey) {
                onToggle(row, true);
              } else if (touch.current && selected.size > 0) {
                onToggle(row, false);
              } else {
                onOpen(row);
              }
            }}
            draggable={Boolean(onDragRows)}
            onDragStart={(e) => {
              const carried = onDragRows?.(row) ?? [row];
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData(MESSAGES_DRAG, String(carried.length));
              e.dataTransfer.setData(
                'text/plain',
                carried.length === 1
                  ? row.latest.subject || '(no subject)'
                  : `${carried.length} conversations`,
              );
              // What follows the pointer: a label, not a picture of the row.
              const ghost = document.createElement('div');
              ghost.className = 'drag-ghost';
              ghost.textContent =
                carried.length === 1
                  ? row.latest.subject || '(no subject)'
                  : `${carried.length} conversations`;
              document.body.appendChild(ghost);
              e.dataTransfer.setDragImage?.(ghost, 12, 12);
              window.setTimeout(() => ghost.remove(), 0);
            }}
            onContextMenu={
              onMenu
                ? (e) => {
                    e.preventDefault();
                    // A finger held down: the browser's long-press menu (Android
                    // raises it while the finger is still down, before or after
                    // the timer) is the same press, and selects, not a menu.
                    if (touch.current && (pressed.current === key || press.current)) {
                      release();
                      if (pressed.current !== key) {
                        pressed.current = key;
                        if (!isSelected) {
                          onToggle(row, false);
                        }
                      }
                      return;
                    }
                    onMenu(row, { x: e.clientX, y: e.clientY });
                  }
                : undefined
            }
          >
            <span
              className="avatar-lg"
              style={{ ['--av-h' as string]: hue(sender.address) }}
              aria-hidden="true"
              title="Select"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(row, e.shiftKey);
              }}
            >
              <span className="av-initials">{isSelected ? '✓' : initials(sender)}</span>
              <span className="av-box">{isSelected ? '✓' : ''}</span>
            </span>
            <input
              type="checkbox"
              className="row-check"
              checked={isSelected}
              aria-label={`Select ${row.latest.subject || 'message'}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggle(row, e.shiftKey);
              }}
              onChange={() => undefined}
            />
            <span className="msg-who">
              {names || '(no sender)'}
              {row.messages.length > 1 && <span className="msg-count">{row.messages.length}</span>}
            </span>
            <time className="msg-when" dateTime={row.latest.date}>
              {listDate(row.latest.date)}
            </time>
            <span className="msg-subject" lang={chineseScript(row.latest.subject)}>
              {row.unread && <span className="dot-unread" aria-label="unread" />}
              {row.latest.subject || '(no subject)'}
            </span>
            <span className="msg-marks">
              {row.hasAttachments && <PaperclipIcon className="mark-icon" />}
              {row.flagged && <FlagIcon className="mark-icon flagged" />}
            </span>
            {row.latest.preview && (
              <span
                className="msg-preview"
                lang={chineseScript(`${row.latest.subject}\n${row.latest.preview}`)}
              >
                {row.latest.preview}
              </span>
            )}
            <span className="row-actions" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                title="Archive (e)"
                aria-label="Archive"
                onClick={() => onAction(row, 'archive')}
              >
                <ArchiveIcon />
              </button>
              <button
                type="button"
                title="Delete (#)"
                aria-label="Delete"
                onClick={() => onAction(row, 'delete')}
              >
                <TrashIcon />
              </button>
              <button
                type="button"
                title={row.unread ? 'Mark read' : 'Mark unread (u)'}
                aria-label={row.unread ? 'Mark read' : 'Mark unread'}
                onClick={() => onAction(row, 'toggle-read')}
              >
                <EnvelopeOpenIcon />
              </button>
              <button
                type="button"
                title={row.flagged ? 'Unflag (s)' : 'Flag (s)'}
                aria-label={row.flagged ? 'Unflag' : 'Flag'}
                className={row.flagged ? 'on' : ''}
                onClick={() => onAction(row, 'toggle-flag')}
              >
                <FlagIcon />
              </button>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function rowKey(m: MessageSummary): string {
  return `${m.folder}:${m.uid}`;
}

/** Placeholder rows while the first page loads. */
export function ListSkeleton(): JSX.Element {
  return (
    <ul className="msg-list" aria-hidden="true">
      {Array.from({ length: 8 }, (_, i) => (
        <li key={i} className="msg-row skeleton">
          <span className="avatar-lg" />
          <span className="msg-who">
            <span className="bone" style={{ width: `${40 + ((i * 17) % 35)}%` }} />
          </span>
          <span className="msg-subject">
            <span className="bone" style={{ width: `${55 + ((i * 23) % 40)}%` }} />
          </span>
          <span className="msg-preview">
            <span className="bone" style={{ width: '85%' }} />
          </span>
        </li>
      ))}
    </ul>
  );
}
